import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { teamService, ROLES, ROLE_LABELS } from '../services/teamService';
import { lobbyService } from '../services/lobbyService';
import { TopBar } from '../components/common/TopBar';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import './TeamDetail.css';

// ── Helpers ────────────────────────────────────────────────
const getRoleInfo = (roleValue) => ROLES.find((r) => r.value === roleValue);

// ── TeamDetail Page ────────────────────────────────────────
const TeamDetail = () => {
  const { id: teamId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const unsubRef = useRef(null);

  const handleTeamUpdate = useCallback((data) => {
    setTeam(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!teamId) return;
    unsubRef.current = teamService.subscribeToTeam(teamId, handleTeamUpdate);
    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, [teamId, handleTeamUpdate]);

  // ── Cleanup Stale Sessions ────────────────────────────────
  useEffect(() => {
    if (team?.activeSession?.lobbyCode) {
      const checkLobby = async () => {
        try {
          const exists = await lobbyService.roomExists(team.activeSession.lobbyCode);
          if (!exists) {
            console.log('Sessione scaduta rilevata, pulizia in corso...');
            await teamService.clearActiveSession(teamId);
          }
        } catch (e) {
          console.error('Errore durante il controllo della lobby:', e);
        }
      };
      checkLobby();
    }
  }, [team?.activeSession?.lobbyCode, teamId]);



  const handleCopyCode = async () => {
    if (!team?.inviteCode) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(team.inviteCode);
      } else {
        // Fallback: create a temporary input
        const textArea = document.createElement("textarea");
        textArea.value = team.inviteCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      console.log('Codice copiato:', team.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Errore durante la copia:', err);
    }
  };

  const handleLeave = async () => {
    try {
      await teamService.leaveTeam(teamId, currentUser.uid);
      navigate('/teams');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleJoinSession = (session) => {
    if (!session) return;
    if (session.type === 'multiplayer') {
      navigate('/multiplayer', { state: { teamSessionCode: session.lobbyCode, teamId } });
    } else if (session.type === 'training') {
      navigate('/training/game', { state: { teamId, teamSessionCode: session.lobbyCode } });
    }
  };

  // ── Loading ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="td-page">
        <TopBar title="Squadra" onBack={() => navigate('/teams')} />
        <LoadingSpinner fullPage />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="td-page">
        <TopBar title="Squadra" onBack={() => navigate('/teams')} />
        <div className="td-loading">Squadra non trovata.</div>
      </div>
    );
  }

  const myMember = team.members?.find((m) => m.userId === currentUser?.uid);
  const myRole = myMember?.role;
  const isCreator = team.createdBy === currentUser?.uid;
  const studyProgress = team.studyProgress || { wordCoverage: 0, progress: { speaker1: 0, speaker2: 0, guesser: 0 } };
  const activeSession = team.activeSession;
  const trainingMembers = team.trainingSession?.members || {};
  const trainingGameState = team.trainingSession?.gameState;
  const trainingGameActive = trainingGameState && ['countdown', 'playing', 'paused', 'finished'].includes(trainingGameState.status);
  const isTrainingActive = Object.keys(trainingMembers).length > 0 || activeSession?.type === 'training' || trainingGameActive;

  // Fill 3 slots
  const slots = [0, 1, 2].map((i) => team.members?.[i] || null);

  return (
    <div className="td-page">
      <TopBar
        title={team.name}
        onBack={() => navigate('/teams')}
        extra={
          <div className="td-header-invite">
            <span className="td-header-invite__label">Codice</span>
            <span className="td-header-invite__code">{team.inviteCode}</span>
            <button
              className={`td-header-btn td-header-btn--copy ${copied ? 'is-copied' : ''}`}
              onClick={handleCopyCode}
              title="Copia codice"
            >
              <span className="td-header-btn__icon">{copied ? '✓' : '📋'}</span>
              <span className="td-header-btn__text">{copied ? 'Copiato' : 'Copia'}</span>
            </button>
          </div>
        }
        actions={
          <div className="td-header-actions">
            {!leaveConfirm ? (
              <button
                className="td-header-btn td-header-btn--leave"
                onClick={() => setLeaveConfirm(true)}
                title="Lascia squadra"
              >
                🚪
              </button>
            ) : (
              <div className="td-header-leave-confirm">
                <span>Lascia?</span>
                <button className="td-leave-confirm__yes" onClick={handleLeave}>Sì</button>
                <button className="td-leave-confirm__no" onClick={() => setLeaveConfirm(false)}>No</button>
              </div>
            )}
          </div>
        }
      />

      <div className="td-content">

        <div className="td-main-grid">
          {/* ── Left Column: Roles ── */}
          <section className="td-section td-left-col">
            <h2 className="td-section__title">Ruoli e Membri</h2>
            <div className="td-roles-list">
              {ROLES.map((role) => {
                const member = team.members?.find((m) => m.role === role.value);
                const isMe = member?.userId === currentUser?.uid;
                return (
                  <div key={role.value} className={`td-role-card ${member ? 'td-role-card--filled' : ''} ${isMe ? 'td-role-card--me' : ''}`}>
                    <div className="td-role-card__main">
                      <span className="td-role-card__icon">{role.icon}</span>
                      <span className="td-role-card__label">{role.label}</span>
                      <span className="td-role-card__name-val">
                        {member ? (
                          <span className="td-role-card__name">{member.name}</span>
                        ) : (
                          <span className="td-role-card__empty">Slot libero</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Right Column: Actions ── */}
          <section className="td-section td-right-col">
            <h2 className="td-section__title">Attività</h2>
            <div className={`td-actions-list ${team.members?.length < 3 ? 'td-actions-list--disabled' : ''}`}>
              <button
                className={`td-action-card td-action-card--multiplayer ${activeSession?.type === 'multiplayer' ? 'is-active' : ''}`}
                onClick={() => {
                  if (activeSession?.type === 'multiplayer') {
                    handleJoinSession(activeSession);
                  } else {
                    navigate('/multiplayer', { state: { teamId } });
                  }
                }}
                id="btn-team-multiplayer"
                disabled={team.members?.length < 3}
              >
                <span className="td-action-card__icon">🌐</span>
                <span className="td-action-card__label">Multiplayer</span>
              </button>


              <button
                className={`td-action-card td-action-card--training ${isTrainingActive ? 'is-active' : ''}`}
                onClick={() => {
                  if (trainingGameActive) {
                    // Sessione di allenamento multiplayer attiva: entra direttamente nel game
                    navigate('/training/game', {
                      state: { teamId, role: myRole, trainingMode: 'multiplayer' },
                    });
                  } else {
                    navigate('/training', { state: { teamId } });
                  }
                }}
                id="btn-team-training"
                disabled={team.members?.length < 3}
              >
                <span className="td-action-card__icon">🏋️</span>
                <span className="td-action-card__label">Allenamento</span>
                {trainingGameActive && (
                  <span className="td-action-card__badge">IN CORSO</span>
                )}
              </button>

              <button
                className="td-action-card td-action-card--study td-action-card--locked"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                id="btn-team-study"
                disabled={true}
              >
                <div className="td-action-card__lock-overlay">
                  <span className="td-action-card__lock-text">Coming Soon</span>
                </div>
                <span className="td-action-card__icon">📖</span>
                <span className="td-action-card__label">Studia</span>
              </button>
            </div>
          </section>
        </div>

        {/* ── Errors ────────────────────────────────── */}
        {error && <p className="td-error">{error}</p>}

      </div>
    </div>
  );
};

export default TeamDetail;
